import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type {
  NeedDetail,
  NeedNote,
  PaginatedNeedNoteList,
  PatchedNeedNote,
  PatchedNeedStatusUpdate,
  PatchedNeedUpdate,
} from '../../../../../js/api';
import { UrgencyLevelEnum, StaffingService } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { ActivityLog } from '../../../components/activity-log';
import { SendItConfirmationDialog } from '../../../components/auto-create-opportunity-dialog';
import { Dialog, DialogBody } from '../../../components/dialog';
import { DrawerCloseButton } from '../../../components/drawer';
import { DrawerSectionHeader, DrawerField } from '../../../components/drawer-components';
import { type CodeDefinitionOption } from '../../../components/dropdown-select';
import { InlineDateEdit } from '../../../components/inline-date-edit';
import { InlineDropdownEdit } from '../../../components/inline-dropdown-edit';
import { InlineTextEdit } from '../../../components/inline-text-edit';
import { PageHeader } from '../../../components/PageHeader';
import { SectionHeader } from '../../../components/SectionHeader';
import { StaffingEntityQuickDetails } from '../../../components/staffing-entity-quick-details';
import { StatusTransitionModal } from '../../../components/status-transition-modal';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsList, TabsButton, TabsContent } from '../../../components/tabs';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { WorkflowScopes } from '../../../constants/workflows';
import {
  useCodeDefinitionsAsMaps,
  useCodeDefinitionsAsOptions,
} from '../../../hooks/useCodeDefinitions';
import { useStatusTransitions } from '../../../hooks/useStatusTransitions';
import type { ActivityStatusHistory, ActivityHistory } from '../../../utils/activity-types';
import { formatDateTo24Hr, parseApiLocalDate, parseFormDate } from '../../../utils/date';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { TRANSITION_COLORS, TRANSITION_OPACITY } from '../../../utils/transitionStyles';
import NeedForm from './NeedForm';
import {
  NEED_NOTES_QUERY_KEY,
  NEED_QUERY_KEY,
  STAFFING_NEEDS_LIST_QUERY_KEY,
  NEED_STATUS_HISTORY_QUERY_KEY,
  NEED_CHANGE_HISTORY_QUERY_KEY,
} from './queryKeys';

const formSchema = z.object({
  urgency_level: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .nullable()
    .optional(),
  current_status: z.string().nullable().optional(),
  current_reason: z.string().nullable().optional(),
  comment: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

type NeedDrawerProps = {
  needId: string;
  onClose?: () => void;
};

function useDebouncedFn<T extends (...args: any[]) => void>(fn: T, delayMs: number) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  return useMemo(() => {
    let t: any;
    const debounced = (...args: any[]) => {
      clearTimeout(t);
      t = setTimeout(() => fnRef.current(...args), delayMs);
    };
    debounced.flush = (...args: any[]) => {
      clearTimeout(t);
      fnRef.current(...args);
    };
    debounced.cancel = () => clearTimeout(t);
    return debounced as T & { flush: T; cancel: () => void };
  }, [delayMs]);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function NeedDrawer({ needId, onClose }: NeedDrawerProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    data: need,
    isLoading: isLoadingNeed,
    error: needError,
  } = useQuery<NeedDetail>({
    queryKey: [NEED_QUERY_KEY, needId],
    queryFn: () => StaffingService.staffingNeedsRetrieve({ id: needId }),
    enabled: !!needId,
  });

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps([
    'Specialty',
    'Degree',
    'WorkType',
    'ShiftRateTypes',
    'JustificationType',
  ]);
  const { data: urgencyLevelOptions } = useCodeDefinitionsAsOptions('UrgencyLevel');

  // Transform API string to form object for urgency_level
  const transformUrgencyLevelApiToForm = useCallback(
    (value: string | null | undefined) => {
      if (!value) return null;
      return urgencyLevelOptions?.find((u) => u.value === value) || { value, label: value };
    },
    [urgencyLevelOptions]
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      urgency_level: transformUrgencyLevelApiToForm(need?.urgency_level),
      current_status: need?.current_status?.code || null,
      current_reason: need?.current_reason?.code || null,
      comment: '',
    },
    mode: 'onChange',
  });

  const { watch, setValue, reset } = form;

  const watchedCurrentStatus = watch('current_status');
  const watchedCurrentReason = watch('current_reason');

  // Track the previous status to detect changes
  const [previousStatus, setPreviousStatus] = useState<string | null>(
    need?.current_status?.code || null
  );

  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState<PatchedNeedStatusUpdate | null>(
    null
  );
  const [pendingUrgencyLevelUpdate, setPendingUrgencyLevelUpdate] =
    useState<PatchedNeedUpdate | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [urgencyLevel, setUrgencyLevel] = useState<CodeDefinitionOption | null>(null);

  const [headerProps, setHeaderProps] = useState<{
    title: string;
    buttonHtml: React.ReactNode;
  } | null>(null);

  const [numShifts, setNumShifts] = useState<string>('');
  const lastSavedNumShiftsRef = useRef<number | null>(null);
  const isSavingNumShiftsRef = useRef(false);
  const isNumShiftsDirtyRef = useRef(false);

  const [targetOn, setTargetOn] = useState<Date | null>(null);
  const lastSavedTargetOnRef = useRef<string | null>(null);
  const isSavingTargetOnRef = useRef(false);
  const isTargetOnDirtyRef = useRef(false);

  // Update form when need data loads
  useEffect(() => {
    if (need) {
      const initialStatus = need.current_status?.code || null;
      reset({
        urgency_level: transformUrgencyLevelApiToForm(need.urgency_level),
        current_status: initialStatus,
        current_reason: need.current_reason?.code || null,
        comment: '',
      });
      setPreviousStatus(initialStatus);
      setUrgencyLevel(transformUrgencyLevelApiToForm(need.urgency_level));

      // Only update local state if not currently saving to avoid overwriting user input
      if (!isSavingTargetOnRef.current) {
        setTargetOn(parseApiLocalDate(need.target_on));
        lastSavedTargetOnRef.current = need.target_on ?? null;
        isTargetOnDirtyRef.current = false;
      }

      if (!isSavingNumShiftsRef.current) {
        setNumShifts(need.num_shifts?.toString() ?? '');
        lastSavedNumShiftsRef.current = need.num_shifts ?? null;
        isNumShiftsDirtyRef.current = false;
      }
    }
  }, [need, reset, transformUrgencyLevelApiToForm, needId]);

  const { data: notesData } = useQuery({
    queryKey: [NEED_NOTES_QUERY_KEY, needId],
    queryFn: () =>
      StaffingService.staffingNeedsNotesList({
        id: needId,
        ordering: '-created_at', // Most recent first
      }),
    enabled: !!needId,
    select: (data: PaginatedNeedNoteList | NeedNote | NeedNote[]) => {
      if (!data) {
        return [];
      }
      // Handle array of notes
      if (Array.isArray(data)) {
        return data;
      }
      // Handle paginated response
      if ('results' in data) {
        return data.results || [];
      }
      // Handle single note
      return [data as NeedNote];
    },
  });

  const { data: statusHistory } = useQuery({
    queryKey: [NEED_STATUS_HISTORY_QUERY_KEY, needId],
    queryFn: () =>
      StaffingService.staffingNeedsStatusHistoryRetrieve({
        id: needId,
        sort: 'desc',
      }),
    enabled: !!needId,
  });

  const { data: changeHistory } = useQuery({
    queryKey: [NEED_CHANGE_HISTORY_QUERY_KEY, needId],
    queryFn: () =>
      StaffingService.staffingNeedsChangeHistoryRetrieve({
        id: needId,
        sort: 'desc',
      }),
    enabled: !!needId,
  });

  const { getStatusConfiguration } = useStatusTransitions({
    currentStatus: need?.current_status?.code || null,
    workflowScope: 'staffing.need',
  });

  const currentStatusRequirements = useMemo(() => {
    if (!watchedCurrentStatus) {
      return { requiresReason: false, requiresNote: false, availableReasons: [] };
    }
    const statusConfiguration = getStatusConfiguration(watchedCurrentStatus);
    return {
      requiresReason: statusConfiguration.require_reason,
      requiresNote: statusConfiguration.require_note,
      availableReasons: statusConfiguration.reasons || [],
    };
  }, [watchedCurrentStatus, getStatusConfiguration]);

  const currentStatusReasonRequirements = useMemo(() => {
    if (!need?.current_status?.code) {
      return { availableReasons: [] };
    }
    const statusConfiguration = getStatusConfiguration(need.current_status.code);
    return {
      availableReasons: statusConfiguration.reasons || [],
    };
  }, [need?.current_status?.code, getStatusConfiguration]);

  // Clear reason when status changes or if it's not valid for the current status
  useEffect(() => {
    // Update previous status tracker
    if (watchedCurrentStatus !== previousStatus) {
      setPreviousStatus(watchedCurrentStatus ?? null);
    }

    if (!watchedCurrentStatus) {
      // If status is cleared, clear reason too
      if (watchedCurrentReason) {
        setValue('current_reason', null);
      }
      return;
    }

    // If status changed, clear reason to force user to select a new one
    const statusChanged = previousStatus !== null && watchedCurrentStatus !== previousStatus;
    if (statusChanged && watchedCurrentReason) {
      setValue('current_reason', null);
      return;
    }

    // If there's no reason set, check if we need to validate
    if (!watchedCurrentReason) return;

    const availableReasonCodes = currentStatusRequirements.availableReasons.map((r) => r.code);

    // Clear reason if: (1) status has no reasons, OR (2) current reason is not in the available list
    const shouldClearReason =
      availableReasonCodes.length === 0 || !availableReasonCodes.includes(watchedCurrentReason);

    if (shouldClearReason) {
      setValue('current_reason', null);
    }
  }, [
    watchedCurrentStatus,
    watchedCurrentReason,
    previousStatus,
    currentStatusRequirements,
    setValue,
  ]);

  const updateUrgencyLevelMutation = useMutation({
    mutationFn: async ({
      id,
      urgency_level,
    }: {
      id: string;
      urgency_level: UrgencyLevelEnum | null;
    }) => {
      return StaffingService.staffingNeedsPartialUpdate({
        id,
        requestBody: {
          urgency_level,
        } as PatchedNeedUpdate,
      });
    },
    onSuccess: async (data, variables) => {
      const { id } = variables;
      queryClient.setQueryData([NEED_QUERY_KEY, id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [NEED_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [STAFFING_NEEDS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [NEED_STATUS_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [NEED_CHANGE_HISTORY_QUERY_KEY, id] }),
      ]);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating urgency level' });
      // Revert on error
      if (need) {
        setUrgencyLevel(transformUrgencyLevelApiToForm(need.urgency_level));
      }
    },
  });

  const updateTargetOnMutation = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: string | null }) => {
      return StaffingService.staffingNeedsPartialUpdate({
        id,
        requestBody: {
          target_on: value,
        } as PatchedNeedUpdate,
      });
    },
    onSuccess: async (data, variables) => {
      const { id } = variables;
      queryClient.setQueryData([NEED_QUERY_KEY, id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [NEED_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [STAFFING_NEEDS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [NEED_STATUS_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [NEED_CHANGE_HISTORY_QUERY_KEY, id] }),
      ]);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating target date' });
      // Revert on error
      if (need) {
        setTargetOn(parseApiLocalDate(need.target_on));
        lastSavedTargetOnRef.current = need.target_on ?? null;
      }
    },
  });

  const updateNumShiftsMutation = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: number | null }) => {
      return StaffingService.staffingNeedsPartialUpdate({
        id,
        requestBody: {
          num_shifts: value,
        } as PatchedNeedUpdate,
      });
    },
    onSuccess: async (data, variables) => {
      const { id } = variables;
      queryClient.setQueryData([NEED_QUERY_KEY, id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [NEED_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [STAFFING_NEEDS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [NEED_STATUS_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [NEED_CHANGE_HISTORY_QUERY_KEY, id] }),
      ]);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating number of shifts' });
      // Revert on error
      if (need) {
        setNumShifts(need.num_shifts?.toString() ?? '');
      }
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PatchedNeedStatusUpdate }) => {
      return StaffingService.staffingNeedsStatusUpdatePartialUpdate({
        id,
        requestBody: data,
      });
    },
    onSuccess: async (data: NeedDetail, variables) => {
      const { id } = variables;
      toast.success('Status updated successfully');
      queryClient.setQueryData([NEED_QUERY_KEY, id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [NEED_NOTES_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [NEED_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [STAFFING_NEEDS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [NEED_STATUS_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [NEED_CHANGE_HISTORY_QUERY_KEY, id] }),
        // Invalidate queue notification badges for this scope
        queryClient.invalidateQueries({
          queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.Need],
        }),
        // Invalidate global menu badges
        queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] }),
      ]);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating status' });
    },
  });

  // Status modal form submit handler (without urgency level)
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleStatusModalConfirm = async (data: {
    statusCode: string | null;
    reasonCode: string | null;
    comment?: string;
  }) => {
    try {
      // Update status/reason if either changed
      const hasComment = data.comment?.trim();
      const statusChanged = data.statusCode !== need?.current_status?.code;
      const reasonChanged = data.reasonCode !== need?.current_reason?.code;

      if (statusChanged || reasonChanged || hasComment) {
        const statusUpdatePayload: PatchedNeedStatusUpdate = {
          code: data.statusCode || undefined,
          reason_code: data.reasonCode || null,
        };

        // Include comment if provided
        if (hasComment && data.comment) {
          statusUpdatePayload.comment = data.comment.trim();
        }

        // Check if we need a confirmation dialog for SENDIT reason_code
        const hasOpenOpportunities =
          need?.linked_opportunities?.some((opp) => opp.current_status?.category_kind !== 'F') ??
          false;

        if (statusUpdatePayload.reason_code === 'SENDIT' && !hasOpenOpportunities) {
          setPendingStatusUpdate(statusUpdatePayload);
          setPendingUrgencyLevelUpdate(null); // No urgency level update in modal
          setShowStatusConfirmation(true);
          setIsStatusModalOpen(false); // Close status modal, will show SENDIT confirmation
          return; // Exit early, will continue after user confirms/cancels
        }

        // No confirmation needed, proceed with update
        await updateStatusMutation.mutateAsync({ id: needId, data: statusUpdatePayload });
        setIsStatusModalOpen(false); // Close modal on success
      } else {
        toast.success('No changes to save');
        setIsStatusModalOpen(false);
      }
    } catch {
      // Errors are handled by mutation onError handlers
    }
  };

  const handleStatusClick = () => {
    setIsStatusModalOpen(true);
  };

  const handleStatusModalClose = () => {
    setIsStatusModalOpen(false);
  };

  const confirmStatusUpdate = async () => {
    if (!pendingStatusUpdate) return;

    try {
      // Proceed with urgency level update if needed
      if (pendingUrgencyLevelUpdate) {
        await updateUrgencyLevelMutation.mutateAsync({
          id: needId,
          urgency_level: pendingUrgencyLevelUpdate.urgency_level as UrgencyLevelEnum | null,
        });
      }

      // Proceed with status update
      await updateStatusMutation.mutateAsync({ id: needId, data: pendingStatusUpdate });

      // Close dialogs and reset state
      setShowStatusConfirmation(false);
      setPendingStatusUpdate(null);
      setPendingUrgencyLevelUpdate(null);
    } catch (error) {
      // Errors are handled by individual mutation onError handlers
      setShowStatusConfirmation(false);
      setPendingStatusUpdate(null);
      setPendingUrgencyLevelUpdate(null);
    }
  };

  const cancelStatusUpdate = async () => {
    // Skip status update but proceed with urgency level update if needed
    if (pendingUrgencyLevelUpdate) {
      try {
        await updateUrgencyLevelMutation.mutateAsync({
          id: needId,
          urgency_level: pendingUrgencyLevelUpdate.urgency_level as UrgencyLevelEnum | null,
        });
      } catch (error) {
        // Errors are handled by individual mutation onError handlers
      }
    }

    // Close dialog and reset state
    setShowStatusConfirmation(false);
    setPendingStatusUpdate(null);
    setPendingUrgencyLevelUpdate(null);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    // Refresh need data after closing modal
    queryClient.invalidateQueries({ queryKey: [NEED_QUERY_KEY, needId] });
    queryClient.invalidateQueries({ queryKey: [STAFFING_NEEDS_LIST_QUERY_KEY] });
  };

  const handleEditSuccess = () => {
    // Refresh need data after successful edit
    queryClient.invalidateQueries({ queryKey: [NEED_QUERY_KEY, needId] });
    queryClient.invalidateQueries({ queryKey: [STAFFING_NEEDS_LIST_QUERY_KEY] });
    setIsEditModalOpen(false);
  };

  const handleUrgencyChange = (selectedOption: CodeDefinitionOption | null) => {
    setUrgencyLevel(selectedOption);
    const urgencyValue = (selectedOption?.value || null) as UrgencyLevelEnum | null;
    updateUrgencyLevelMutation.mutate({
      id: needId,
      urgency_level: urgencyValue,
    });
  };

  const handleTargetOnChange = (date: Date | null) => {
    setTargetOn(date);
    isTargetOnDirtyRef.current = true;
  };

  const targetOnCommit = useCallback(
    async (val?: Date | null) => {
      const dateToSave = val !== undefined ? val : targetOn;
      const dateStr = parseFormDate(dateToSave);

      if (
        dateStr === lastSavedTargetOnRef.current ||
        isSavingTargetOnRef.current ||
        !isTargetOnDirtyRef.current
      ) {
        return;
      }

      try {
        isSavingTargetOnRef.current = true;
        await updateTargetOnMutation.mutateAsync({ id: needId, value: dateStr });
        lastSavedTargetOnRef.current = dateStr;
        isTargetOnDirtyRef.current = false;
      } catch (e) {
        console.error('Target date save failed', e);
      } finally {
        isSavingTargetOnRef.current = false;
      }
    },
    [updateTargetOnMutation, targetOn, needId]
  );

  const debouncedTargetOnCommit = useDebouncedFn(targetOnCommit, 600);

  const handleTargetOnBlur = (value?: Date | null) => {
    debouncedTargetOnCommit.flush(value);
  };

  const handleNumShiftsChange = (value: string) => {
    setNumShifts(value);
    isNumShiftsDirtyRef.current = true;
  };

  const numShiftsCommit = useCallback(
    async (val?: number | null) => {
      const numericValue = val !== undefined ? val : numShifts === '' ? null : Number(numShifts);
      if (
        numericValue === lastSavedNumShiftsRef.current ||
        isSavingNumShiftsRef.current ||
        !isNumShiftsDirtyRef.current
      )
        return;

      try {
        isSavingNumShiftsRef.current = true;
        await updateNumShiftsMutation.mutateAsync({ id: needId, value: numericValue });
        lastSavedNumShiftsRef.current = numericValue;
        isNumShiftsDirtyRef.current = false;
      } catch (e) {
        console.error('Num shifts save failed', e);
      } finally {
        isSavingNumShiftsRef.current = false;
      }
    },
    [updateNumShiftsMutation, numShifts, needId]
  );

  const debouncedNumShiftsCommit = useDebouncedFn(numShiftsCommit, 600);

  const handleNumShiftsBlur = (value: string) => {
    if (!need) {
      return;
    }

    // Convert to number or null
    const numericValue = value === '' ? null : Number(value);

    // Validate that it's a number if not empty
    if (value !== '' && isNaN(Number(value))) {
      return;
    }

    debouncedNumShiftsCommit.flush(numericValue);
  };

  // Flush when leaving the page or unmounting (Safari-friendly)
  useEffect(() => {
    const onPageHide = () => {
      debouncedNumShiftsCommit.flush();
      debouncedTargetOnCommit.flush();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onPageHide);
      // Flush on unmount to ensure pending changes are saved
      debouncedNumShiftsCommit.flush();
      debouncedTargetOnCommit.flush();
    };
  }, [debouncedNumShiftsCommit, debouncedTargetOnCommit]);

  // Save on click/tap outside (covers “no blur” cases)
  useEffect(() => {
    const handler = () => {
      // For InlineEdit components, they have their own click-outside logic
      // but we want to ensure any pending debounced commits are flushed
      // if the user clicks anywhere else.
      debouncedNumShiftsCommit.flush();
      debouncedTargetOnCommit.flush();
    };
    // Use capture phase to ensure we catch it before any stops propagation
    document.addEventListener('mousedown', handler, true);
    document.addEventListener('touchstart', handler, true);
    return () => {
      document.removeEventListener('mousedown', handler, true);
      document.removeEventListener('touchstart', handler, true);
    };
  }, [debouncedNumShiftsCommit, debouncedTargetOnCommit]);

  if (isLoadingNeed) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading need details...</div>
      </div>
    );
  }

  if (needError) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-stoplight-danger">
          Error loading need details. Please try again.
        </div>
      </div>
    );
  }

  if (!need) {
    return null;
  }

  const specialtyMap = codeDefinitionMaps?.Specialty || {};
  const degreeMap = codeDefinitionMaps?.Degree || {};
  const workTypeMap = codeDefinitionMaps?.WorkType || {};
  const shiftRateTypeMap = codeDefinitionMaps?.ShiftRateTypes || {};
  const justificationMap = codeDefinitionMaps?.JustificationType || {};

  return (
    <>
      <StatusTransitionModal
        open={isStatusModalOpen}
        onClose={handleStatusModalClose}
        onConfirm={handleStatusModalConfirm}
        initialStatusCode={need?.current_status?.code || null}
        workflowScope="staffing.need"
        isPending={updateStatusMutation.isPending}
        currentReasonCode={need?.current_reason?.code || null}
        currentStatusTitle={need?.current_status?.title || null}
      />
      <Dialog
        open={isEditModalOpen}
        onClose={handleEditModalClose}
        className="tw:!max-w-6xl tw:!max-h-[calc(100vh-4rem)] tw:!flex tw:!flex-col tw:!p-0"
      >
        <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:overflow-hidden">
          {headerProps && (
            <div className="tw:flex-shrink-0">
              <PageHeader
                className="tw:rounded-t-2xl"
                title={headerProps.title}
                buttonHtml={headerProps.buttonHtml}
              />
            </div>
          )}
          <div className="tw:flex-1 tw:overflow-y-auto tw:min-h-0">
            <DialogBody className="tw:mt-0">
              <NeedForm
                needId={needId}
                forceEditMode={true}
                onSuccess={handleEditSuccess}
                onCancel={handleEditModalClose}
                renderHeader={false}
                onHeaderRender={setHeaderProps}
              />
            </DialogBody>
          </div>
        </div>
      </Dialog>
      <div className="tw:flex tw:flex-col tw:mt-6">
        {/* Top Section */}
        <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-12 tw:gap-6">
          {/* Need Details + Close (stacked: same row; sm+: grid cells) */}
          <div className="tw:flex tw:items-start tw:justify-between tw:gap-4 tw:sm:contents">
            <div className="tw:flex-1 tw:min-w-0 tw:sm:col-span-6">
              <StaffingEntityQuickDetails
                code={need.code}
                facilityName={need.facility?.name}
                location={getFacilityLocationDisplayValue(need.facility)}
                specialty={specialtyMap[need.specialty] || need.specialty}
              />
            </div>
            {onClose && (
              <DrawerCloseButton onClick={onClose} className="tw:shrink-0 tw:sm:hidden" />
            )}
          </div>

          {/* Quick Edit form */}
          <div className="tw:flex tw:flex-col tw:sm:col-span-5">
            <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:items-center">
              <div className="tw:text-sm tw:font-semibold">Status:</div>
              <div>
                <button
                  type="button"
                  onClick={handleStatusClick}
                  className={`tw:m-o tw:px-[5px] tw:py-0 tw:text-left tw:flex tw:items-center tw:gap-1 tw:cursor-pointer tw:hover:opacity-60 ${TRANSITION_OPACITY}`}
                >
                  {need.current_status?.title}{' '}
                  <i className="fas fa-edit tw:text-xs tw:text-brand-green-0" />
                </button>
              </div>
              {currentStatusReasonRequirements.availableReasons.length > 0 && (
                <>
                  <div className="tw:text-sm tw:font-semibold">
                    {need.current_status?.code === 'CLOSED' ? 'Reason:' : 'Labor Tier:'}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={handleStatusClick}
                      className={`tw:m-o tw:px-[5px] tw:py-0 tw:text-left tw:flex tw:items-center tw:gap-1 tw:cursor-pointer tw:hover:opacity-60 ${TRANSITION_OPACITY}`}
                    >
                      {need.current_reason?.title}{' '}
                      <i className="fas fa-edit tw:text-xs tw:text-brand-green-0" />
                    </button>
                  </div>
                </>
              )}
              <div className="tw:text-sm tw:font-semibold">Urgency:</div>
              <div>
                <InlineDropdownEdit
                  value={urgencyLevel}
                  onChange={handleUrgencyChange}
                  options={urgencyLevelOptions}
                  placeholder="--"
                  small
                  editOn="hover"
                  isClearable
                  disabled={updateUrgencyLevelMutation.isPending}
                />
              </div>
              <div className="tw:text-sm tw:font-semibold">Shifts to Fill:</div>
              <InlineTextEdit
                value={numShifts}
                onChange={handleNumShiftsChange}
                onBlur={handleNumShiftsBlur}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    const value = (e.target as HTMLInputElement).value;
                    handleNumShiftsBlur(value);
                  }
                }}
                placeholder="Enter number of shifts..."
                displayValue={(val) => val || '--'}
                small
                editOn="hover"
                disabled={
                  need.current_status?.title === 'Closed' || updateNumShiftsMutation.isPending
                }
                type="text"
                inputMode="numeric"
              />
              <div className="tw:text-sm tw:font-semibold">Target Date:</div>
              <div>
                <InlineDateEdit
                  value={targetOn}
                  onChange={handleTargetOnChange}
                  onBlur={handleTargetOnBlur}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTargetOnBlur(targetOn);
                  }}
                  placeholder="Select target date"
                  small
                  editOn="hover"
                  disabled={updateTargetOnMutation.isPending}
                />
              </div>
            </div>
          </div>

          {/* Close Button (grid layout only) */}
          {onClose && (
            <div className="tw:hidden tw:sm:flex tw:justify-end tw:items-start tw:sm:col-span-1 tw:sm:justify-self-end">
              <DrawerCloseButton onClick={onClose} />
            </div>
          )}
        </div>

        {/* Tabs Section */}
        <Tabs defaultTab="details">
          <TabsList>
            <TabsButton tab="details">Details</TabsButton>
            <TabsButton tab="opportunities">Opportunities</TabsButton>
          </TabsList>

          <TabsContent tab="details">
            <div className="tw:flex tw:justify-between tw:items-start tw:gap-6">
              <div className="tw:flex-1 tw:space-y-4">
                {/* Basic Information Section */}
                <section>
                  <DrawerSectionHeader
                    text="Basic Information"
                    icon={
                      <i className="fas fa-info-circle tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Justification"
                      value={
                        need.justification
                          ? justificationMap[need.justification] || need.justification
                          : null
                      }
                    />
                    <DrawerField label="Description" value={need.description} whitespacePreLine />
                    <DrawerField
                      label="Additional Facilities"
                      value={
                        need.alternate_facilities &&
                        Array.isArray(need.alternate_facilities) &&
                        need.alternate_facilities.length > 0
                          ? need.alternate_facilities.map((facility) => facility.name).join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Job Requisition"
                      value={(() => {
                        const hasKey = !!need.job_requisition_key;
                        const hasUrl = !!need.job_requisition_url;

                        if (hasKey && hasUrl) {
                          // Both have values: show key as link text
                          return (
                            <a
                              href={need.job_requisition_url!}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tw:text-brand-green-0 tw:hover:underline tw:dark:text-brand-green-1"
                            >
                              {need.job_requisition_key}
                            </a>
                          );
                        }
                        if (hasUrl) {
                          // Only URL: show "View" as link
                          return (
                            <a
                              href={need.job_requisition_url!}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tw:text-brand-green-0 tw:hover:underline tw:dark:text-brand-green-1"
                            >
                              View
                            </a>
                          );
                        }
                        // Only key or neither: show key as string (or null)
                        return need.job_requisition_key || null;
                      })()}
                    />
                  </div>
                </section>

                {/* Professional Requirements Section */}
                <section>
                  <DrawerSectionHeader
                    text="Professional Requirements"
                    icon={
                      <i className="fas fa-stethoscope tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Degrees"
                      value={
                        need.degrees && need.degrees.length > 0
                          ? need.degrees.map((deg) => degreeMap[deg] || deg).join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Availability"
                      value={
                        need.work_types && need.work_types.length > 0
                          ? need.work_types
                              .map((workType) => workTypeMap[workType] || workType)
                              .join(', ')
                          : null
                      }
                    />
                  </div>
                </section>

                {/* Need Settings Section */}
                <section>
                  <DrawerSectionHeader
                    text="Need Settings"
                    icon={
                      <i className="fas fa-cog tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Shift Rate Types"
                      value={
                        need.shift_rate_types && need.shift_rate_types.length > 0
                          ? need.shift_rate_types
                              // eslint-disable-next-line sonarjs/cognitive-complexity
                              .map((shiftRateType) => {
                                const shiftRateTypeLabel =
                                  shiftRateTypeMap[shiftRateType] || shiftRateType;
                                // Map shift rate type to max rate field
                                const shiftTypeToRateKey: Record<string, string> = {
                                  day: 'max_rate_day',
                                  night: 'max_rate_night',
                                  call: 'max_rate_call',
                                  '24h': 'max_rate_24h',
                                };
                                const rateKey = shiftTypeToRateKey[shiftRateType];
                                const rate =
                                  rateKey === 'max_rate_day'
                                    ? need.max_rate_day
                                    : rateKey === 'max_rate_night'
                                      ? need.max_rate_night
                                      : rateKey === 'max_rate_call'
                                        ? need.max_rate_call
                                        : rateKey === 'max_rate_24h'
                                          ? need.max_rate_24h
                                          : null;
                                const rateDisplay = rate ? `: $${rate}/hr` : '';
                                return `${shiftRateTypeLabel}${rateDisplay}`;
                              })
                              .join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Created"
                      value={
                        need.created_at
                          ? formatDateTo24Hr(need.created_at, 'MM/dd/yyyy HH:mm')
                          : null
                      }
                    />
                    <DrawerField
                      label="Last Updated"
                      value={
                        need.updated_at
                          ? formatDateTo24Hr(need.updated_at, 'MM/dd/yyyy HH:mm')
                          : null
                      }
                    />
                  </div>
                </section>
              </div>
              {need.current_status?.title !== 'Closed' && (
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(true)}
                  className={`tw:text-brand-green-0 tw:hover:text-brand-green-0/80 ${TRANSITION_COLORS} tw:cursor-pointer`}
                  aria-label="Edit need"
                >
                  <i className="fa-solid fa-pen-to-square tw:text-lg" />
                </button>
              )}
            </div>
          </TabsContent>
          <TabsContent tab="opportunities">
            <div className="tw:mt-3">
              <div className="tw:font-semibold tw:border-b tw:border-zinc-300 tw:dark:border-zinc-600 tw:text-zinc-900 tw:dark:text-zinc-300 pb-1">
                Linked Opportunities
              </div>
              <div className="tw:mt-2">
                {need.linked_opportunities && need.linked_opportunities.length > 0 ? (
                  <div className="tw:space-y-2">
                    {need.linked_opportunities.map((opportunity) => (
                      <div
                        key={opportunity.id}
                        className="tw:flex tw:items-center tw:justify-between tw:py-1 tw:border-b tw:border-zinc-100 last:tw:border-b-0 tw:dark:border-zinc-700"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/app/organization/opportunities?id=${opportunity.id}`)
                          }
                          className="tw:text-sm tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:hover:underline tw:text-left tw:truncate tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                          title="View opportunity"
                        >
                          {opportunity.code || 'Untitled opportunity'}
                        </button>
                        <span className="tw:ml-2 tw:inline-flex tw:items-center">
                          <StatusPill
                            scope={WorkflowScopes.Need}
                            status={opportunity.current_status || 'Unknown'}
                          />
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400">
                    No linked opportunities.
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="tw:mt-2 tw:p-2 tw:bg-brand-green-3 tw:rounded-lg">
          <SectionHeader className="tw:mb-2 tw:p-1 tw:text-brand-green-0" headerText="Activity" />
          <ActivityLog
            enableCommentsFilter={true}
            enableStatusChangesFilter={true}
            enableUpdatesFilter={true}
            enableEventsFilter={false}
            notes={notesData}
            statusHistory={statusHistory as ActivityStatusHistory[] | undefined}
            changeHistory={changeHistory as ActivityHistory[] | undefined}
            noteMutations={{
              createNote: async (data) => {
                return StaffingService.staffingNeedsNotesCreate({
                  id: needId,
                  requestBody: {
                    need: needId,
                    comment: data.comment,
                  } as NeedNote,
                });
              },
              updateNote: async (noteId: string, data: PatchedNeedNote) => {
                return StaffingService.staffingNeedsNotePartialUpdate({
                  id: noteId,
                  requestBody: data,
                });
              },
              deleteNote: async (noteId: string) => {
                return StaffingService.staffingNeedsNoteDestroy({
                  id: noteId,
                });
              },
              notesQueryKey: [NEED_NOTES_QUERY_KEY, needId],
              extraInvalidateQueryKeys: [[NEED_STATUS_HISTORY_QUERY_KEY, needId]],
            }}
            composerPlaceholder="Enter a comment..."
          />
        </div>

        <SendItConfirmationDialog
          open={showStatusConfirmation}
          onClose={cancelStatusUpdate}
          onConfirm={confirmStatusUpdate}
        />
      </div>
    </>
  );
}
